<template>
  <div id="results_section">
    <div class="results">
      <h5 v-if="hasResults">
        Showing
        <span :inner-html.prop="resultsDisplayed" @click="clearKeywords"></span>
        of <span style="padding-left: 6px;">{{ totalCount }}</span> documents
      </h5>

      <div v-if="hasAnyFilters" class="filter-strings">
        <span class="filter-heading">Filtered By:</span>
        <template v-if="filters.length > 0">
          <span
            v-for="(filter, index) in filters"
            :key="index"
            class="filter-string"
            @click="$emit('clearFilter', filter.id || filter.title)"
          >
            {{ filter.title }}
          </span>
        </template>
        <span
          v-if="startDate"
          class="filter-string"
          @click="$emit('clearDate', 'start_date')"
        >
          Start Date: {{ startDate }}
        </span>
        <span
          v-if="endDate"
          class="filter-string"
          @click="$emit('clearDate', 'end_date')"
        >
          End Date: {{ endDate }}
        </span>

        <a id="clear-all-filters" href="#" @click="$emit('clearAllFilters')">Clear All</a>
      </div>
    </div>
    <template v-if="hasResults">
      <div id="results">
        <article v-for="item in items.data" :key="item.id" class="group">
          <h3 v-if="item.description">
            <a :href="item.url">{{ item.title }}</a>
          </h3>
          <h3 v-else>
            <a :href="item.file.url">{{ item.title }}</a>
          </h3>

          <div>
            <!-- Date -->
            <div class="date">
              {{ item.document_date }} | {{ item.document_type.label }}
              <span v-if="item.version === 'Draft' || item.version === 'Final'">
                | {{ item.version }}
              </span>
            </div>

            <!-- Related items -->
            <div
              v-if="item.related.length"
              class="categories"
              :inner-html.prop="item.related | joinPostedInCategories"
            ></div>

            <!-- Description -->
            <div :inner-html.prop="item.summary"></div>
          </div>
          <a class="btn btn-details me-1" :href="item.url"> View Details </a>
          <a
            v-if="item.file.url"
            class="btn btn-download"
            :href="item.file.url"
          >
            Download
          </a>
        </article>

        <!-- Pagination -->
        <paginate
          :page-count="pagination.total_pages"
          :page-range="5"
          :margin-pages="2"
          :click-handler="(e) => $emit('navigate', e)"
          container-class="page_link"
          page-class="current"
        >
          ></paginate
        >
      </div>
      <loading v-if="loading" />
    </template>

    <loading v-else-if="loading" />

    <template v-else>
      <div v-if="keywords" class="info">
        There are no results that match your search for the keywords
        <span class="remove_keyword" @click="clearKeywords"
          >"{{ keywords }}"</span
        >
      </div>
      <div v-else class="info">
        There are no results that match your search.
      </div>
    </template>
  </div>
</template>

<script>
import Loading from './Loading.vue'
import Paginate from 'vuejs-paginate'

export default {
  components: {
    Paginate,
    Loading,
  },

  props: {
    keywords: {
      type: [String, null],
      default: null,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Object,
      default: {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    startDate: [String, null],
    endDate: [String, null],
  },

  computed: {
    pagination() {
      if (this.items) {
        return this.items.meta ? this.items.meta.pagination : {}
      }

      return {}
    },

    hasResults() {
      return this.pagination.total > 0
    },

    resultsDisplayed() {
      let showingFrom = this.currentPage * this.itemsPerPage - (this.itemsPerPage - 1)
      let showingTo = this.totalCount
      
      if (this.totalCount > this.itemsPerPage) {
        if (!((this.currentPage * this.itemsPerPage) > this.totalCount)) {
          showingTo = this.currentPage * this.itemsPerPage
        }
      }

      let label = `${showingFrom} - ${showingTo}`

      if (this.keywords) {
        label += ` matching your search for <a class="remove_keyword">"${this.keywords}"</a>`
      }

      return label
    },

    currentPage() {
      return this.pagination.current_page
    },

    itemsOnPageCount() {
      return this.pagination.count
    },

    itemsPerPage() {
      return this.pagination.per_page
    },

    totalCount() {
      return this.pagination.total
    },

    hasAnyFilters() {
      return this.filters.length > 0 || this.startDate || this.endDate
    },
  },

  filters: {
    joinPostedInCategories(items) {
      if (items) {
        return (
          'Posted in ' +
          items
            .map(
              (item) =>
                `<a href="${item.url}">${item.title}</a> (${item.section})`
            )
            .join(', ')
        )
      } else {
        return ''
      }
    },
  },

  methods: {
    clearKeywords() {
      this.$emit('clearKeywords', true)
    },
  },
}
</script>

<style>
.results {
  margin-top: 0;
  padding-bottom: 16px;
  border-bottom: 1px solid rgb(62, 68, 70);
}

.btn-details {
  background: none repeat scroll 0 0 #37a1d7;
  border: 3px solid #37a1d7;
  border-radius: 4px;
  color: #fff;
  margin: 0.5em 0;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  padding: 5px;
  position: relative;
}

.btn-details:hover {
  opacity: 0.8;
  color: #fff;
}

.btn-download {
  background-color: #fff;
  color: rgb(66, 163, 221);
  border: 3px solid #e0e0e0;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin: 1em 0;
  padding: 5px;
  position: relative;
}

.btn-download:hover {
  background: #e0e0e0;
  color: #ed8700;
  cursor: pointer;
}

ul.page_link > li {
  display: inline;
  padding: 3px;
}
ul.page_link {
  list-style: none;
}

.remove_keyword {
  cursor: pointer;
  text-decoration: none;
}

.remove_keyword::after {
  content: '\f057';
  font-family: 'FontAwesome';
  margin-left: 5px;
}
</style>
